import { useEffect, useRef, useState } from 'react';
import { Colors, Flex, IconShare, Typography } from '@banhala/ui-kit';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useGoodsDetail } from '../../../../queries/useGoodsDetail';
import { useAffiliateGoods } from '../../../../queries/useAffiliateGoods';
import { useInstanceId, useScreenshot } from 'shared/src/hooks';
import {
  bridge,
  isAndroid,
  isNil,
  isNotNil,
  isWeb,
  localizeNumber,
} from 'shared/src/utils';
import { scheduleOnRN } from 'react-native-worklets';
import { useLogClickShareGoods } from './hooks/useLogClickShareGoods';
import { useLogGoodsDefaultAnalyticsParams } from '../../../../hooks/useLogGoodsAnalyticsEvent';

const CLOSED_BUTTON_SIZE = isAndroid ? 46 : 41;
// 측정 전 초기 fallback 값
const OPENED_BUTTON_SIZE_FULL = isAndroid || isWeb ? 92 : 80;

export const ShareRewardButton: React.FC<{
  showNudging: boolean;
  nudgeTextColor?: string;
}> = props => {
  const animValue = useSharedValue<number>(CLOSED_BUTTON_SIZE);
  const getDefaultAnalyticsParams = useLogGoodsDefaultAnalyticsParams();
  const [reward, setReward] = useState(0);
  const [rewardForMeasure, setRewardForMeasure] = useState<number>(0);

  const animationStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // 측정된 실제 텍스트 너비 (fallback: 상수 값)
  const fullWidthRef = useRef<number>(OPENED_BUTTON_SIZE_FULL);
  const rewardForMeasureRef = useRef<number>(0);

  const logClickShareGoods = useLogClickShareGoods();
  const instanceId = useInstanceId();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: affiliateGoods } = useAffiliateGoods();

  useEffect(() => {
    return () => {
      if (animationStartTimerRef.current)
        clearTimeout(animationStartTimerRef.current);
    };
  }, []);

  useScreenshot({
    instanceId,
    onCapture: () => {
      if (isNotNil(goodsDetail)) {
        bridge.openGoodsShare({
          is_by_screenshot: true,
          goods_sno: goodsDetail.goods.sno,
          affiliate_reward: {
            expected_reward: affiliateGoods?.expected_reward ?? 0,
            share_nudging: affiliateGoods?.share_nudging ?? null,
            logging: affiliateGoods?.logging ?? null,
          },

          logging_analytics: {
            ...getDefaultAnalyticsParams(),
          },
        });
      }
    },
  });

  // 숨겨진 측정 뷰의 onLayout 콜백에서 호출 — 두 뷰가 모두 측정되면 애니메이션 시작
  const tryStartAnimation = () => {
    if (animationStartTimerRef.current != null) return;

    if (isWeb) return;

    animationStartTimerRef.current = setTimeout(() => {
      const r = rewardForMeasureRef.current;
      const fullW = fullWidthRef.current;

      // 초기 애니메이션: 닫힌 상태 유지(200ms) → 리워드 세팅 → 열기(200ms) → 유지(1200ms) → 닫기(200ms+200ms)
      animValue.value = withSequence(
        withTiming(
          CLOSED_BUTTON_SIZE,
          { duration: 200, easing: Easing.inOut(Easing.quad) },
          () => {
            scheduleOnRN(setReward, r);
          },
        ),
        withTiming(fullW, {
          duration: 200,
          easing: Easing.inOut(Easing.circle),
        }),
        withDelay(
          1200,
          withTiming(fullW, { duration: 200 }, () => {
            scheduleOnRN(setReward, 0);
          }),
        ),
        withTiming(CLOSED_BUTTON_SIZE, {
          duration: 200,
          easing: Easing.inOut(Easing.quad),
        }),
      );
    }, 500);
  };

  useEffect(() => {
    if (isNil(goodsDetail)) return;
    const expectedReward = affiliateGoods?.expected_reward ?? 0;

    if (expectedReward === 0) return;
    // 측정 뷰를 렌더링하여 onLayout으로 실제 텍스트 너비를 측정한 뒤 애니메이션 시작
    rewardForMeasureRef.current = expectedReward;
    setRewardForMeasure(expectedReward);
  }, [goodsDetail, affiliateGoods]);

  const styles = useAnimatedStyle(() => {
    return {
      width: animValue.value,
      height: 16,
      overflow: 'hidden',
    };
  });

  return (
    <>
      {/* "최대 X원 받기" 너비 측정용 숨겨진 뷰 */}
      {rewardForMeasure > 0 && (
        <Flex
          position={'absolute'}
          opacity={0}
          pointerEvents="none"
          onLayout={e => {
            fullWidthRef.current = Math.ceil(e.nativeEvent.layout.width);
            tryStartAnimation();
          }}
        >
          <Typography type={'subtitle4'}>
            최대{' '}
            <Typography type={'subtitle4'} color={'content_blue_primary'}>
              {localizeNumber(rewardForMeasure)}원
            </Typography>{' '}
            받기
          </Typography>
        </Flex>
      )}
      <Flex.Row
        padding={{ horizontal: 10, vertical: 6 }}
        gap={4}
        backgroundColor={props.showNudging ? undefined : 'bg_secondary'}
        borderWidth={props.showNudging ? 1 : undefined}
        borderColor={props.showNudging ? 'border_transparent' : undefined}
        borderRadius={'medium'}
        overflow={'hidden'}
        onPress={() => {
          logClickShareGoods();
          if (isNotNil(goodsDetail)) {
            bridge.openGoodsShare({
              is_by_screenshot: false,
              goods_sno: goodsDetail.goods.sno,
              affiliate_reward: {
                expected_reward: affiliateGoods?.expected_reward ?? 0,
                share_nudging: affiliateGoods?.share_nudging ?? null,
                logging:
                  affiliateGoods?.logging != null
                    ? affiliateGoods.logging
                    : undefined,
              },
              logging_analytics: {
                ...getDefaultAnalyticsParams(),
              },
            });

            if (isWeb) {
              bridge.toast('링크가 클립보드에 복사되었어요');
            }
          }
        }}
        testID={'공유버튼'}
      >
        <IconShare size={16} fill={Colors.content_secondary} />
        <Animated.View style={styles}>
          {reward > 0 ? (
            <Typography type={'subtitle4'}>
              최대{' '}
              <Typography
                type={'subtitle4'}
                color={
                  props.showNudging
                    ? 'content_pink_primary'
                    : 'content_blue_primary'
                }
                rawColor={props.showNudging ? props.nudgeTextColor : undefined}
              >
                {localizeNumber(reward)}원
              </Typography>
              {' 받기'}
            </Typography>
          ) : (
            <Typography type={'subtitle4'}>공유하기</Typography>
          )}
        </Animated.View>
      </Flex.Row>
    </>
  );
};
